import { c as _c } from "react/compiler-runtime";
import { Text } from "@flat-ui";
import { Box, ButtonBase, Stack } from "@mui-wrapper";
import { getMarkupStatusLabel } from "@pages/project-details/project-markups/status/markup-status-utils";
import { MARKUPS_DUE_SOON_COLOR, MARKUPS_OVERDUE_COLOR } from "@pages/project-details/project-overview/markups-summary/markups-summary-chart";
import { getMarkupStatusChipColor } from "@pages/project-details/project-overview/markups-summary/markups-summary-colors";
import { DUE_SOON_FILTER_ID, OVERDUE_FILTER_ID, OVERDUE_TRACKED_STATUSES } from "@pages/project-details/project-overview/markups-summary/markups-summary-utils";
import { CommonStyles } from "@styles/common-styles";
import { PALETTE_VARS } from "@styles/palette-vars";
import { Link } from "react-router-dom";

/** Focus ring of the legend links */
const FOCUS_OUTLINE = `2px solid ${PALETTE_VARS.primary.main}`;

/** Styles of the overdue and due soon links, which look like plain text */
const DEADLINE_LINK_SX = {
  font: "inherit",
  verticalAlign: "baseline",
  borderRadius: 0.5,
  // Above the row overlay of the main link
  zIndex: 1,
  "&:hover": {
    textDecoration: "underline"
  },
  "&.Mui-focusVisible": {
    outline: FOCUS_OUTLINE
  }
};
/** Overdue and due soon amounts of a status, separated by a dot when both are present */
function MarkupsSummaryLegendDeadlines(t0) {
  const $ = _c(20);
  const {
    overdueCount,
    dueSoonCount,
    filter,
    getLink
  } = t0;
  let t1;
  if ($[0] !== filter || $[1] !== getLink) {
    t1 = getLink({
      ...filter,
      dueDate: OVERDUE_FILTER_ID
    }, "legend");
    $[0] = filter;
    $[1] = getLink;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const overdueLink = t1;
  let t2;
  if ($[3] !== filter || $[4] !== getLink) {
    t2 = getLink({
      ...filter,
      dueDate: DUE_SOON_FILTER_ID
    }, "legend");
    $[3] = filter;
    $[4] = getLink;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const dueSoonLink = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      whiteSpace: "nowrap"
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== overdueCount || $[8] !== overdueLink) {
    t4 = overdueCount > 0 && <ButtonBase disableRipple={true} component={Link} to={overdueLink.href} onClick={overdueLink.onClick} sx={{
      ...DEADLINE_LINK_SX,
      color: "error.main"
    }}><var>{overdueCount}</var> overdue</ButtonBase>;
    $[7] = overdueCount;
    $[8] = overdueLink;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== dueSoonCount || $[11] !== overdueCount) {
    t5 = overdueCount > 0 && dueSoonCount > 0 && <Box component="span" sx={{
      color: "text.secondary",
      mx: 0.75
    }}>·</Box>;
    $[10] = dueSoonCount;
    $[11] = overdueCount;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== dueSoonCount || $[14] !== dueSoonLink) {
    t6 = dueSoonCount > 0 && <ButtonBase disableRipple={true} component={Link} to={dueSoonLink.href} onClick={dueSoonLink.onClick} sx={{
      ...DEADLINE_LINK_SX,
      color: "warning.main"
    }}><var>{dueSoonCount}</var> due soon</ButtonBase>;
    $[13] = dueSoonCount;
    $[14] = dueSoonLink;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = <Text variant="bodyS" sx={t3}>{t4}{t5}{t6}</Text>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}

/**
 * Single row of the legend. The whole row is clickable through the label link,
 * whose overlay stretches over the row, while the deadline links stay on top of it.
 */
function MarkupsSummaryLegendItem(t0) {
  const $ = _c(30);
  const {
    color,
    label,
    count,
    filter,
    getLink,
    overdueCount: t1,
    dueSoonCount: t2
  } = t0;
  const overdueCount = t1 === undefined ? 0 : t1;
  const dueSoonCount = t2 === undefined ? 0 : t2;
  let t3;
  if ($[0] !== filter || $[1] !== getLink) {
    t3 = getLink(filter, "legend");
    $[0] = filter;
    $[1] = getLink;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const link = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "relative",
      alignItems: "center",
      gap: 1,
      px: 0.5,
      mx: -0.5,
      borderRadius: 1,
      "&:hover": {
        backgroundColor: PALETTE_VARS.action.hover
      }
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== color) {
    t5 = <Box sx={{
      width: "0.75rem",
      height: "0.75rem",
      borderRadius: "50%",
      flexShrink: 0,
      backgroundColor: color
    }} />;
    $[4] = color;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      position: "static",
      flexGrow: 1,
      justifyContent: "flex-start",
      "&::after": {
        content: "\"\"",
        position: "absolute",
        inset: 0,
        borderRadius: 1
      },
      "&.Mui-focusVisible::after": {
        outline: FOCUS_OUTLINE
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== label) {
    t7 = <Text variant="bodyM">{label}</Text>;
    $[7] = label;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== link.href || $[10] !== link.onClick || $[11] !== t7) {
    t8 = <ButtonBase disableRipple={true} component={Link} to={link.href} onClick={link.onClick} sx={t6}>{t7}</ButtonBase>;
    $[9] = link.href;
    $[10] = link.onClick;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== dueSoonCount || $[14] !== filter || $[15] !== getLink || $[16] !== overdueCount) {
    t9 = (overdueCount > 0 || dueSoonCount > 0) && <MarkupsSummaryLegendDeadlines overdueCount={overdueCount} dueSoonCount={dueSoonCount} filter={filter} getLink={getLink} />;
    $[13] = dueSoonCount;
    $[14] = filter;
    $[15] = getLink;
    $[16] = overdueCount;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = count ? undefined : "text.secondary";
  let t11;
  if ($[18] !== t10) {
    t11 = {
      minWidth: "2rem",
      textAlign: "right",
      color: t10
    };
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== count) {
    t12 = count ? <var>{count}</var> : "\u2013";
    $[20] = count;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t12) {
    t13 = <Text variant="heading16" sx={t11}>{t12}</Text>;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t5 || $[27] !== t8 || $[28] !== t9) {
    t14 = <Stack direction="row" sx={t4}>{t5}{t8}{t9}{t13}</Stack>;
    $[25] = t13;
    $[26] = t5;
    $[27] = t8;
    $[28] = t9;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}

/** Legend of the markups summary chart, with the amount of markups per status */
export function MarkupsSummaryLegend(t0) {
  const $ = _c(25);
  const {
    summary,
    getLink
  } = t0;
  let t1;
  if ($[0] !== summary) {
    t1 = summary.reduce(_temp, 0);
    $[0] = summary;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totalOverdue = t1;
  let t2;
  if ($[2] !== summary) {
    t2 = summary.reduce(_temp2, 0);
    $[2] = summary;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const totalDueSoon = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      gap: 1,
      flexGrow: 1,
      minWidth: "14rem"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== getLink || $[6] !== summary) {
    let t5;
    if ($[8] !== getLink) {
      t5 = t6 => {
        const {
          status,
          count,
          overdueCount: overdueCount_0,
          dueSoonCount: dueSoonCount_0
        } = t6;
        return <MarkupsSummaryLegendItem key={status} color={getMarkupStatusChipColor(status)} label={getMarkupStatusLabel(status)} count={count} filter={{
          statuses: [status]
        }} getLink={getLink} overdueCount={overdueCount_0} dueSoonCount={dueSoonCount_0} />;
      };
      $[8] = getLink;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = summary.map(t5);
    $[5] = getLink;
    $[6] = summary;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      gap: 1,
      borderTop: CommonStyles.Borders.divider,
      pt: 1
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      statuses: OVERDUE_TRACKED_STATUSES,
      dueDate: DUE_SOON_FILTER_ID
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== getLink || $[13] !== totalDueSoon) {
    t7 = <MarkupsSummaryLegendItem color={MARKUPS_DUE_SOON_COLOR} label="Due within 7 days" count={totalDueSoon} filter={t6} getLink={getLink} />;
    $[12] = getLink;
    $[13] = totalDueSoon;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      statuses: OVERDUE_TRACKED_STATUSES,
      dueDate: OVERDUE_FILTER_ID
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== getLink || $[17] !== totalOverdue) {
    t9 = <MarkupsSummaryLegendItem color={MARKUPS_OVERDUE_COLOR} label="Overdue" count={totalOverdue} filter={t8} getLink={getLink} />;
    $[16] = getLink;
    $[17] = totalOverdue;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t7 || $[20] !== t9) {
    t10 = <Stack sx={t5}>{t7}{t9}</Stack>;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t4) {
    t11 = <Stack sx={t3}>{t4}{t10}</Stack>;
    $[22] = t10;
    $[23] = t4;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
function _temp2(total_0, t0) {
  const {
    dueSoonCount
  } = t0;
  return total_0 + dueSoonCount;
}
function _temp(total, t0) {
  const {
    overdueCount
  } = t0;
  return total + overdueCount;
}